import { Navigate, useLocation, useSearchParams } from "react-router-dom"
import { useAuthSession } from "@/features/auth/hooks/use-auth-session"

/** Paths safe for post-login `?redirect=` (same-origin, no open redirect). */
// eslint-disable-next-line react-refresh/only-export-components -- shared auth route-guard helper imported across the app (rsvp-button, TierSelectionSection, guard tests); splitting would require editing out-of-scope importers
export function isSafeRedirectPath(path: string | null): boolean {
  if (!path) return false
  return path.startsWith("/") && !path.startsWith("//")
}

/**
 * Where to send an already-authenticated user leaving an auth-only page.
 */
// eslint-disable-next-line react-refresh/only-export-components -- shared auth route-guard helper imported across the app; splitting would require editing out-of-scope importers
export function resolveAuthenticatedRedirect(searchParams: URLSearchParams): string {
  const redirect = searchParams.get("redirect")
  if (isSafeRedirectPath(redirect)) {
    return redirect!
  }
  return "/"
}

// eslint-disable-next-line react-refresh/only-export-components -- shared auth route-guard helper imported across the app; splitting would require editing out-of-scope importers
export function buildUnauthenticatedRedirectUrl(pathname: string, search: string = ""): string {
  const returnTo = `${pathname}${search}`
  if (returnTo && returnTo !== "/") {
    return `/auth?redirect=${encodeURIComponent(returnTo)}`
  }
  return "/auth"
}

function AuthLoadingSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary" />
    </div>
  )
}

/**
 * Protects routes that require a signed-in user.
 */
export function RequireAuth({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthSession()
  const location = useLocation()

  if (isLoading) {
    return <AuthLoadingSpinner />
  }

  if (!isAuthenticated) {
    return (
      <Navigate
        to={buildUnauthenticatedRedirectUrl(location.pathname, location.search)}
        replace
      />
    )
  }

  return <>{children}</>
}

/**
 * Auth-only routes (e.g. `/auth`): redirect signed-in users to home or `?redirect=`.
 */
export function RedirectIfAuthenticated({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthSession()
  const [searchParams] = useSearchParams()

  if (isLoading) {
    return <AuthLoadingSpinner />
  }

  if (isAuthenticated) {
    return <Navigate to={resolveAuthenticatedRedirect(searchParams)} replace />
  }

  return <>{children}</>
}

export default RequireAuth
